/**
 * MetersFormSchemaVersion
 * Schema-based version of MetersForm
 */

import React, { useState, useEffect, useRef } from 'react';
import { Zap } from 'lucide-react';
import BuiltInFormRenderer from '../DynamicForm/BuiltInFormRenderer';
import PhotoGalleryModal from '../PhotoGalleryModal';
import { metersFormSchema } from '../../schemas/metersFormSchema';
import { useRetrofitPhotoUpload } from '../../hooks/useRetrofitPhotoUpload';
import { RetrofitSurveyData, getRetrofitSurveyAddress } from '../../utils/retrofitSurveyStorage';
import { UseAutosaveReturn } from '../../hooks/useAutosave';
import { FieldSchema } from '../../types/formSchema';
import { deletePhotoFromSupabase } from '../../utils/supabaseStorage';

export interface MetersFormProps {
  onNavigateNext: () => void;
  onNavigateBack: () => void;
  surveyData?: RetrofitSurveyData;
  surveyId?: string;
  onDataUpdate?: (updater: (prev: RetrofitSurveyData) => RetrofitSurveyData, sectionId?: string) => void;
  autosave?: UseAutosaveReturn;
  onSaveImmediately?: () => Promise<string>;
  onGalleryStateChange?: (isOpen: boolean) => void;
  customFields?: FieldSchema[];
  hiddenFields?: string[];
  hiddenSections?: string[];
  sectionOrder?: string[];
  builtInFieldOrder?: string[];
}

interface DisplayablePhotoData {
  id: string;  // PhotoGalleryModal expects 'id'
  displayId: string;
  dataUrl?: string;
  url?: string;
  format?: string;
  uploading?: boolean;
  error?: string;
  note?: string;
}

const MetersFormSchemaVersion: React.FC<MetersFormProps> = ({
  onNavigateNext,
  onNavigateBack,
  surveyData,
  surveyId,
  onDataUpdate,
  autosave,
  onSaveImmediately,
  onGalleryStateChange,
  customFields = [],
  hiddenFields = [],
  hiddenSections = [],
  sectionOrder = [],
  builtInFieldOrder = []
}) => {
  // State for form data (built-in fields only)
  const [formData, setFormData] = useState<Record<string, any>>({});
  
  // State for custom field data (separate from built-in)
  const [customFieldData, setCustomFieldData] = useState<Record<string, any>>({});
  
  // State for photos
  const [photos, setPhotos] = useState<Record<string, DisplayablePhotoData[]>>({});
  
  // State for gallery
  const [selectedGallery, setSelectedGallery] = useState<string | null>(null);
  
  const isInternalUpdate = useRef(false);
  
  // Initialize photo upload queue with explicit surveyId and property name for better file organization
  const propertyName = surveyData ? getRetrofitSurveyAddress(surveyData) : undefined;
  const { queuePhotoUpload } = useRetrofitPhotoUpload(surveyId || '', propertyName);
  
  // Load built-in field data from surveyData on mount
  useEffect(() => {
    if (isInternalUpdate.current) {
      console.log('[Meters] Skipping main data reload - internal update');
      return;
    }
    
    if (surveyData?.initial_questionnaire) {
      console.log('[MetersForm] Loading built-in data from surveyData');
      
      const questionnaire = surveyData.initial_questionnaire;
      
      setFormData({
        hasGasMeter: questionnaire.hasGasMeter ?? null,
        gasMeterLocation: questionnaire.gasMeterLocation ?? '',
        hasElectricMeter: questionnaire.hasElectricMeter ?? null,
        electricMeterLocation: questionnaire.electricMeterLocation ?? '',
        hasConsumerBoard: questionnaire.hasConsumerBoard ?? null,
        consumerBoardLocation: questionnaire.consumerBoardLocation ?? '',
        hasStopTap: questionnaire.hasStopTap ?? null,
        stopTapLocation: questionnaire.stopTapLocation ?? '',
      });
      
      // Load photos if they exist
      if (questionnaire.photos) {
        const loadedPhotos: Record<string, DisplayablePhotoData[]> = {};
        
        // Helper to convert photo format
        const convertPhotos = (photoData: any): DisplayablePhotoData[] => {
          if (!photoData) return [];
          if (Array.isArray(photoData)) return photoData;
          if (photoData.photos && Array.isArray(photoData.photos)) return photoData.photos;
          return [];
        };
        
        loadedPhotos.gasMeterEarthBonding = convertPhotos(questionnaire.photos.gasMeterEarthBonding);
        loadedPhotos.electricMeter = convertPhotos(questionnaire.photos.electricMeter);
        loadedPhotos.consumerBoard = convertPhotos(questionnaire.photos.consumerBoard);
        loadedPhotos.stopTap = convertPhotos(questionnaire.photos.stopTap);
        
        setPhotos(loadedPhotos);
      }
    }
  }, [surveyData]);
  
  // Load custom field values
  useEffect(() => {
    if (isInternalUpdate.current) {
      console.log('[Meters] Skipping custom field reload - internal update');
      return;
    }
    
    if (surveyData?.initial_questionnaire && customFields && customFields.length > 0) {
      console.log('[MetersForm] Loading custom field data');
      const existingData: Record<string, any> = {};
      customFields.forEach(field => {
        // Load the field value
        const value = (surveyData.initial_questionnaire as any)?.[field.id];
        if (value !== undefined) {
          existingData[field.id] = value;
        }
        
        // Also load photos if they exist (stored with _photos suffix)
        const photosFieldId = `${field.id}_photos`;
        const photoValue = (surveyData.initial_questionnaire as any)?.[photosFieldId];
        if (photoValue !== undefined) {
          console.log('[MetersForm] Loading photos for custom field:', field.id, '→', photosFieldId);
          existingData[photosFieldId] = photoValue;
        }
      });
      if (Object.keys(existingData).length > 0) {
        console.log('[MetersForm] Loaded custom field data:', Object.keys(existingData));
        setCustomFieldData(existingData);
      }
    }
  }, [surveyData?.initial_questionnaire, customFields]);
  
  // Reset internal update flag after all data loaders have run
  useEffect(() => {
    if (isInternalUpdate.current) {
      console.log('[Meters] Resetting internal update flag');
      isInternalUpdate.current = false;
    }
  }, [surveyData]);
  
  // Handle built-in form data changes
  const handleFormDataChange = (fieldId: string, value: any) => {
    console.log('[MetersForm] ========== handleFormDataChange START ==========');
    console.log('[MetersForm] 📝 Built-in field:', fieldId);
    console.log('[MetersForm] 📝 Value:', value);
    
    setFormData(prev => {
      console.log('[MetersForm] 📝 Previous formData:', prev);
      const updated = { ...prev, [fieldId]: value };
      console.log('[MetersForm] 📝 Updated formData:', updated);
      
      // Trigger autosave
      if (onDataUpdate) {
        console.log('[MetersForm] 📝 Triggering autosave...');
        setTimeout(() => {
          isInternalUpdate.current = true;
          console.log('[MetersForm] 📝 Autosave executing - saving to initial_questionnaire');
          onDataUpdate((prevData) => ({
            ...prevData,
            initial_questionnaire: {
              ...prevData.initial_questionnaire,
              ...updated,
              ...photos,
              ...customFieldData
            }
          }), 'initial_questionnaire');
        }, 50);
      }
      
      console.log('[MetersForm] ========== handleFormDataChange END ==========');
      return updated;
    });
  };
  
  // Handle custom field changes
  const handleCustomFieldChange = (fieldId: string, value: any) => {
    console.log('[MetersForm] ========== handleCustomFieldChange START ==========');
    console.log('[MetersForm] 🎨 Custom field:', fieldId);
    console.log('[MetersForm] 🎨 Value type:', typeof value, Array.isArray(value) ? `(array of ${value.length})` : '');
    console.log('[MetersForm] 🎨 Value:', value);
    
    setCustomFieldData(prev => {
      console.log('[MetersForm] 🎨 Previous customFieldData:', prev);
      const updated = { ...prev, [fieldId]: value };
      console.log('[MetersForm] 🎨 Updated customFieldData:', updated);
      
      // Trigger autosave
      if (onDataUpdate) {
        console.log('[MetersForm] 🎨 Triggering autosave...');
        setTimeout(() => {
          isInternalUpdate.current = true;
          console.log('[MetersForm] 🎨 Autosave executing - saving to initial_questionnaire');
          console.log('[MetersForm] 🎨 Saving: formData + photos + customFieldData');
          onDataUpdate((prevData) => ({
            ...prevData,
            initial_questionnaire: {
              ...prevData.initial_questionnaire,
              ...formData,
              ...photos,
              ...updated
            }
          }), 'initial_questionnaire');
        }, 50);
      }
      
      console.log('[MetersForm] ========== handleCustomFieldChange END ==========');
      return updated;
    });
  };
  
  // Handle photo changes
  const handlePhotosChange = (fieldId: string, newPhotos: any[]) => {
    console.log('[MetersForm] ========== handlePhotosChange START ==========');
    console.log('[MetersForm] 📷 Field ID:', fieldId);
    console.log('[MetersForm] 📷 New photos count:', newPhotos.length);
    console.log('[MetersForm] 📷 New photos:', newPhotos);
    console.log(`[MetersForm] Handling photos change for ${fieldId}:`, newPhotos);
    
    // Transform photos to proper format
    const newPhotoData = newPhotos.map(photo => {
      let dataUrl = photo.dataUrl || photo.base64String;
      
      // Ensure proper data URL format
      if (dataUrl && !dataUrl.startsWith('data:')) {
        const format = photo.format || 'jpeg';
        dataUrl = `data:image/${format};base64,${dataUrl}`;
      }
      
      const id = `${Date.now()}_${Math.random()}`;
      return {
        id,  // For PhotoGalleryModal
        displayId: id,  // For consistency
        dataUrl,
        format: photo.format || 'jpeg',
        uploading: true
      };
    });
    
    setPhotos(prev => {
      const updated = {
        ...prev,
        [fieldId]: [...(prev[fieldId] || []), ...newPhotoData]
      };
      
      // Queue uploads
      newPhotoData.forEach(photo => {
        queuePhotoUpload(
          photo.displayId,
          photo.dataUrl,
          'initial_questionnaire',
          fieldId,
          async (url: string) => {
            // Upload success - only update state with CDN URL
            console.log(`[MetersForm] Photo uploaded successfully: ${fieldId}`);
            setPhotos(current => ({
              ...current,
              [fieldId]: (current[fieldId] || []).map(p =>
                p.displayId === photo.displayId
                  ? { ...p, url, uploading: false }
                  : p
              )
            }));
          },
          (error: string) => {
            // Upload error
            console.error(`[MetersForm] Photo upload failed:`, error);
            setPhotos(current => ({
              ...current,
              [fieldId]: (current[fieldId] || []).map(p =>
                p.displayId === photo.displayId
                  ? { ...p, uploading: false, error }
                  : p
              )
            }));
          }
        );
      });
      
      // Trigger autosave
      if (onDataUpdate) {
        setTimeout(() => {
          isInternalUpdate.current = true;
          onDataUpdate(prevData => ({
            ...prevData,
            initial_questionnaire: {
              ...prevData.initial_questionnaire,
              ...formData,
              ...updated,
              ...customFieldData
            }
          }), 'initial_questionnaire');
        }, 50);
      }
      
      return updated;
    });
  };
  
  // Handle gallery close
  const handleGalleryClose = () => {
    setSelectedGallery(null);
  };

  // Remove photo function
  const removePhoto = async (photoType: string, displayIdToRemove: string) => {
    console.log(`[Meters] 🗑️ Removing photo from ${photoType}, displayId: ${displayIdToRemove}`);
    
    const currentPhotos = photos[photoType] || [];
    const photoToDelete = currentPhotos.find((p: any) => p.displayId === displayIdToRemove);
    
    if (photoToDelete) {
      let filePathToDelete = (photoToDelete as any).fileName;
      
      if (!filePathToDelete && photoToDelete.url && photoToDelete.url.includes('/storage/v1/object/public/')) {
        const urlParts = photoToDelete.url.split('/storage/v1/object/public/')[1];
        if (urlParts) {
          const [bucket, ...pathParts] = urlParts.split('/');
          filePathToDelete = pathParts.join('/');
        }
      }
      
      if (filePathToDelete) {
        try {
          await deletePhotoFromSupabase(filePathToDelete, 'retrofit-survey-images');
          console.log(`[Meters] ✅ Deleted from storage`);
        } catch (error) {
          console.warn(`[Meters] ⚠️ Failed to delete from storage:`, error);
        }
      }
    }
    
    const filteredPhotos = currentPhotos.filter((p: any) => p.displayId !== displayIdToRemove);
    setPhotos(prev => ({ ...prev, [photoType]: filteredPhotos }));
    
    if (onDataUpdate) {
      onDataUpdate((prevData: any) => ({
        ...prevData,
        initial_questionnaire: {
          ...prevData.initial_questionnaire,
          photos: {
            ...prevData.initial_questionnaire?.photos,
            ...photos,
            [photoType]: filteredPhotos
          }
        }
      }), 'initial_questionnaire');
    }
  };

  // Combine all data for form value handling
  const allFormData = { ...formData, ...customFieldData };
  
  return (
    <>
      <BuiltInFormRenderer
        schema={metersFormSchema}
        formData={allFormData}
        photos={photos}
        onFormDataChange={(fieldId, value) => {
          console.log('[MetersForm] ========== onFormDataChange CALLED ==========');
          console.log('[MetersForm] 🔀 Field ID:', fieldId);
          console.log('[MetersForm] 🔀 Value type:', typeof value, Array.isArray(value) ? `(array of ${value.length})` : '');
          console.log('[MetersForm] 🔀 Value:', value);
          
          // Check if it's a custom field (strip _photos suffix for comparison)
          const baseFieldId = fieldId.replace(/_photos$/, '');
          const isCustomField = customFields.some(f => f.id === baseFieldId);
          console.log('[MetersForm] 🔀 Base field ID:', baseFieldId);
          console.log('[MetersForm] 🔀 Is custom field?', isCustomField);
          console.log('[MetersForm] 🔀 Custom fields:', customFields.map(f => f.id));
          
          if (isCustomField) {
            console.log('[MetersForm] 🔀 Routing to handleCustomFieldChange');
            handleCustomFieldChange(fieldId, value);
          } else {
            console.log('[MetersForm] 🔀 Routing to handleFormDataChange (built-in)');
            handleFormDataChange(fieldId, value);
          }
          console.log('[MetersForm] ========== onFormDataChange END ==========');
        }}
        onPhotosChange={handlePhotosChange}
        onGalleryOpen={setSelectedGallery}
        selectedGallery={selectedGallery}
        customFields={customFields}
        hiddenFields={hiddenFields}
        hiddenSections={hiddenSections}
        sectionOrder={sectionOrder}
        builtInFieldOrder={builtInFieldOrder}
        navigationButtons={{
          previous: {
            label: '← Back',
            onClick: onNavigateBack
          },
          next: {
            label: 'Next →',
            onClick: onNavigateNext
          }
        }}
        formIcon={<Zap className="w-8 h-8" />}
      />
      
      {/* Photo Gallery Modal */}
      {selectedGallery && photos[selectedGallery] && (
        <PhotoGalleryModal
          isOpen={true}
          onClose={handleGalleryClose}
          photos={photos[selectedGallery].map((photo: any) => ({
            id: photo.displayId,
            url: photo.url || photo.dataUrl,
            uploading: photo.uploading,
            error: photo.error
          }))}
          sectionTitle={`${selectedGallery} Photos`}
          onDeletePhoto={(photoId) => removePhoto(selectedGallery, photoId)}
          onUpdateNote={(photoId, note) => {
            setPhotos(current => ({
              ...current,
              [selectedGallery]: current[selectedGallery].map(p =>
                p.displayId === photoId ? { ...p, note } : p
              )
            }));
            
            // Update surveyData
            if (onDataUpdate) {
              onDataUpdate((prevData) => ({
                ...prevData,
                initial_questionnaire: {
                  ...prevData.initial_questionnaire,
                  photos: {
                    ...prevData.initial_questionnaire?.photos,
                    [selectedGallery]: {
                      photos: (photos[selectedGallery] || []).map(p =>
                        p.displayId === photoId ? { ...p, note } : p
                      )
                    }
                  }
                }
              }), 'initial_questionnaire');
            }
          }}
          onGalleryStateChange={onGalleryStateChange}
        />
      )}
    </>
  );
};

export default MetersFormSchemaVersion;
